import dayjs from "../formacion/dayjs-timezone";
import {
  SchoolTeacherProgress,
  TeacherProgramProgress,
  TeacherProgressApp,
  TeacherSummary,
} from "../../services/teacherProgress/teacherProgress.types";

export const ALL_GRADES = "ALL_GRADES";
export const ALL_SUBJECTS = "ALL_SUBJECTS";

export type GradeFilter = number | typeof ALL_GRADES;
export type SubjectFilter = number | typeof ALL_SUBJECTS;

export const MIN_SEARCH_LENGTH = 2;
export const DISPLAY_TIMEZONE = "America/Mexico_City";

export const APP_LABELS: Record<TeacherProgressApp, string> = {
  WEB: "Profes Web",
  MOBILE: "Profes App",
  DESKTOP: "Profes Desktop",
};

interface TeacherName {
  firstName: string;
  lastName: string;
}

export const formatWeekLabel = (week: string): string =>
  week.includes("-") ? `Semanas ${week}` : `Semana ${week}`;

export const formatLastViewedAt = (iso: string): string =>
  dayjs.utc(iso).tz(DISPLAY_TIMEZONE).format("DD/MM/YYYY · HH:mm");

export const formatTeacherName = ({ firstName, lastName }: TeacherName) =>
  [firstName, lastName].filter(Boolean).join(" ");

export const teacherInitials = ({ firstName, lastName }: TeacherName) =>
  [firstName, lastName]
    .map((part) => part.trim().charAt(0).toUpperCase())
    .join("");

const compareLabels = (a: { label: string }, b: { label: string }) =>
  a.label.localeCompare(b.label, "es", { numeric: true });

const uniqueOptions = (entries: { id: number; name: string }[]) =>
  Array.from(new Map(entries.map((entry) => [entry.id, entry.name])))
    .map(([value, label]) => ({ value, label }))
    .sort(compareLabels);

export const buildGradeOptions = (programs: TeacherProgramProgress[]) => [
  { value: ALL_GRADES as GradeFilter, label: "Todos los grados" },
  ...uniqueOptions(programs.map((program) => program.grade)),
];

export const buildSubjectOptions = (programs: TeacherProgramProgress[]) => [
  { value: ALL_SUBJECTS as SubjectFilter, label: "Todas las asignaturas" },
  ...uniqueOptions(programs.map((program) => program.subject)),
];

export const filterPrograms = (
  programs: TeacherProgramProgress[],
  grade: GradeFilter,
  subject: SubjectFilter,
) =>
  programs.filter(
    (program) =>
      (grade === ALL_GRADES || program.grade.id === grade) &&
      (subject === ALL_SUBJECTS || program.subject.id === subject),
  );

export const programsNeedingTitle = (
  programs: TeacherProgramProgress[],
): Set<number> => {
  const keyOf = (program: TeacherProgramProgress) =>
    `${program.grade.id}:${program.subject.id}`;
  const counts = new Map<string, number>();
  programs.forEach((program) =>
    counts.set(keyOf(program), (counts.get(keyOf(program)) ?? 0) + 1),
  );
  return new Set(
    programs
      .filter((program) => (counts.get(keyOf(program)) ?? 0) > 1)
      .map((program) => program.programId),
  );
};

interface SchoolOption {
  id: number;
  name: string;
  parent_id?: number | null;
  deleted_at?: Date | string | null;
}

export const buildSchoolOptions = (schools: SchoolOption[]) => {
  const live = schools.filter((school) => school.deleted_at == null);
  const names = new Map(live.map((school) => [school.id, school.name]));
  return live
    .map((school) => {
      const parentName =
        school.parent_id != null ? names.get(school.parent_id) : undefined;
      return {
        value: school.id,
        label: parentName ? `${school.name} (${parentName})` : school.name,
      };
    })
    .sort(compareLabels);
};

export interface SchoolProgressRow {
  key: string;
  teacher: TeacherSummary;
  program: TeacherProgramProgress | null;
}

export const flattenSchoolTeachers = (
  teachers: SchoolTeacherProgress[],
): SchoolProgressRow[] =>
  teachers.flatMap(({ teacher, programs }): SchoolProgressRow[] =>
    programs.length === 0
      ? [{ key: `${teacher.id}`, teacher, program: null }]
      : programs.map((program) => ({
          key: `${teacher.id}:${program.programId}`,
          teacher,
          program,
        })),
  );

export const summarizeSchoolTeachers = (teachers: SchoolTeacherProgress[]) => ({
  teachers: teachers.length,
  inactive: teachers.filter(({ programs }) => programs.length === 0).length,
});

const normalizeText = (text: string) =>
  text
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase();

export const filterSchoolRows = (
  rows: SchoolProgressRow[],
  grade: GradeFilter,
  subject: SubjectFilter,
  name: string,
) => {
  const words = normalizeText(name).split(/\s+/).filter(Boolean);
  return rows.filter(({ teacher, program }) => {
    if (grade !== ALL_GRADES && program?.grade.id !== grade) return false;
    if (subject !== ALL_SUBJECTS && program?.subject.id !== subject) {
      return false;
    }
    const haystack = normalizeText(
      `${formatTeacherName(teacher)} ${teacher.email ?? ""}`,
    );
    return words.every((word) => haystack.includes(word));
  });
};
